import Navigation from "@/components/Navigation";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import SEO from "@/components/SEO";
import { ShieldCheck } from "lucide-react";

const Privacy = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Privacy Policy | George Dives"
        description="Privacy Policy for George Dives. Learn how we collect, use, and protect your personal information."
        keywords={['privacy policy', 'data protection', 'GDPR', 'personal information']}
      />
      
      <Navigation />
      <FloatingUserMenu />

      {/* Header */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-blue-700 to-blue-900 text-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 mb-4">
            <ShieldCheck className="w-4 h-4" />
            <span className="text-sm font-semibold">Privacy</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-300">
            Last updated: December 10, 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            
            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">1. Introduction</h2>
            <p className="text-slate-700 mb-4">
              George Dives ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our website and services.
            </p>
            <p className="text-slate-700 mb-4">
              By using our website and services, you consent to the practices described in this Privacy Policy. If you do not agree with this policy, please do not use our services.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">2.1 Personal Information</h3>
            <p className="text-slate-700 mb-4">
              We collect personal information that you voluntarily provide when you:
            </p>
            <ul className="list-disc list-inside text-slate-700 mb-4">
              <li>Contact us via email, phone, or contact forms</li>
              <li>Book diving courses or services</li>
              <li>Create an account on our website</li>
              <li>Submit reviews or testimonials</li>
              <li>Subscribe to our newsletter</li>
            </ul>
            <p className="text-slate-700 mb-4">
              This information may include:
            </p>
            <ul className="list-disc list-inside text-slate-700 mb-4">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Country of residence</li>
              <li>Date of birth or age</li>
              <li>Diving certification level</li>
              <li>Medical information (for diving safety purposes)</li>
              <li>Payment information (processed securely through third-party providers)</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">2.2 Automatically Collected Information</h3>
            <p className="text-slate-700 mb-4">
              When you visit our website, we automatically collect certain information about your device and browsing behavior, including:
            </p>
            <ul className="list-disc list-inside text-slate-700 mb-4">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-700 mb-4">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc list-inside text-slate-700 mb-4">
              <li><strong>Service Delivery:</strong> To provide diving courses, process bookings, and deliver services</li>
              <li><strong>Communication:</strong> To respond to inquiries, send booking confirmations, and provide customer support</li>
              <li><strong>Safety:</strong> To assess medical fitness and ensure participant safety during diving activities</li>
              <li><strong>Marketing:</strong> To send promotional materials, newsletters, and updates (with your consent)</li>
              <li><strong>Website Improvement:</strong> To analyze usage patterns and improve website functionality</li>
              <li><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">4. Cookies and Tracking Technologies</h2>
            <p className="text-slate-700 mb-4">
              We use cookies and similar technologies to enhance your experience on our website. Cookies are small data files stored on your device that help us:
            </p>
            <ul className="list-disc list-inside text-slate-700 mb-4">
              <li>Remember your preferences and settings</li>
              <li>Understand how you use our website</li>
              <li>Improve website performance and functionality</li>
              <li>Deliver targeted advertising (through third-party services)</li>
            </ul>
            <p className="text-slate-700 mb-4">
              You can control cookie settings through your browser preferences. However, disabling cookies may affect website functionality.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">5. Third-Party Services</h2>
            <p className="text-slate-700 mb-4">
              We use third-party services to enhance our website and services:
            </p>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">5.1 Google Analytics</h3>
            <p className="text-slate-700 mb-4">
              We use Google Analytics to track and analyze website traffic. Google Analytics collects information anonymously and reports website trends without identifying individual visitors. For more information, visit Google's Privacy Policy.
            </p>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">5.2 Payment Processors</h3>
            <p className="text-slate-700 mb-4">
              Payment information is processed securely through third-party payment providers. We do not store complete credit card information on our servers.
            </p>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">5.3 Email Services</h3>
            <p className="text-slate-700 mb-4">
              We use email service providers to send communications. Your email address is stored securely and used only for intended purposes.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">6. Data Sharing and Disclosure</h2>
            <p className="text-slate-700 mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-slate-700 mb-4">
              <li><strong>Partner Dive Centers:</strong> We share necessary information with dive centers to facilitate your booking and ensure safety</li>
              <li><strong>Service Providers:</strong> We share information with trusted third-party service providers who assist in website operation and service delivery</li>
              <li><strong>Legal Requirements:</strong> We may disclose information when required by law or to protect our rights, safety, or property</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">7. Data Security</h2>
            <p className="text-slate-700 mb-4">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc list-inside text-slate-700 mb-4">
              <li>Secure servers and encrypted connections (SSL/TLS)</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication</li>
              <li>Staff training on data protection</li>
            </ul>
            <p className="text-slate-700 mb-4">
              However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">8. Data Retention</h2>
            <p className="text-slate-700 mb-4">
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. Medical and safety-related information may be retained for longer periods as required by diving industry regulations.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">9. Your Rights</h2>
            <p className="text-slate-700 mb-4">
              Under applicable data protection laws (including GDPR for EU residents), you have the following rights:
            </p>
            <ul className="list-disc list-inside text-slate-700 mb-4">
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Objection:</strong> Object to processing of your information</li>
              <li><strong>Restriction:</strong> Request restriction of processing</li>
              <li><strong>Portability:</strong> Request transfer of your information to another service</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing (where applicable)</li>
            </ul>
            <p className="text-slate-700 mb-4">
              To exercise these rights, please contact us using the information provided below.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">10. Children's Privacy</h2>
            <p className="text-slate-700 mb-4">
              Our services are not directed to children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe we have collected information about your child, please contact us immediately.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">11. International Data Transfers</h2>
            <p className="text-slate-700 mb-4">
              Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-slate-700 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page with an updated "Last Updated" date. We encourage you to review this policy periodically.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">13. Contact Us</h2>
            <p className="text-slate-700 mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact us:
            </p>
            <ul className="list-disc list-inside text-slate-700 mb-4">
              <li>Email: info@georgedives.com</li>
              <li>Phone/WhatsApp: +30 698 794 6278</li>
              <li>Website: https://georgedives.com/contact</li>
            </ul>

            {/* Data Breach Notification */}
            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-12">Data Breach Notification</h2>
            <p className="text-slate-700 mb-4">
              In the event of a data breach that affects your personal information, we will:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
              <li>Notify you within <strong>72 hours</strong> of discovering the breach (as required by GDPR)</li>
              <li>Provide details about what information was affected</li>
              <li>Explain the steps we're taking to address the breach</li>
              <li>Recommend actions you can take to protect yourself</li>
              <li>Report the breach to relevant supervisory authorities as required by law</li>
            </ul>
            <p className="text-slate-700 mb-4">
              We maintain security measures including encryption, access controls, and regular security audits to minimize the risk of data breaches.
            </p>

            {/* International Data Transfers */}
            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-12">International Data Transfers</h2>
            <p className="text-slate-700 mb-4">
              George Dives operates globally and may transfer your personal information to countries outside your residence. When we transfer data internationally, we ensure:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
              <li><strong>Adequate protection:</strong> We only transfer data to countries that provide an adequate level of data protection or use appropriate safeguards</li>
              <li><strong>Standard contractual clauses:</strong> We use EU-approved standard contractual clauses for transfers from the EU/EEA</li>
              <li><strong>Service provider agreements:</strong> Our service providers (hosting, email, analytics) are required to protect your data in accordance with applicable laws</li>
              <li><strong>Compliance with local laws:</strong> We comply with data protection laws in all jurisdictions where we operate</li>
            </ul>

            {/* Do Not Sell - CCPA Requirement */}
            <h2 id="do-not-sell" className="text-2xl font-bold text-slate-900 mb-4 mt-12">Do Not Sell My Personal Information (CCPA)</h2>
            <div className="p-6 bg-amber-50 border-l-4 border-amber-500 rounded mb-4">
              <p className="text-slate-900 font-semibold mb-2">
                We do NOT sell your personal information.
              </p>
              <p className="text-slate-700">
                George Dives does not sell, rent, or trade your personal information to third parties for monetary or other valuable consideration. This applies to all users, including California residents covered by the California Consumer Privacy Act (CCPA).
              </p>
            </div>
            <p className="text-slate-700 mb-4">
              Under CCPA, California residents have the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
              <li>Know what personal information we collect and how it's used</li>
              <li>Request deletion of your personal information</li>
              <li>Opt-out of the sale of personal information (though we don't sell data)</li>
              <li>Not be discriminated against for exercising these rights</li>
            </ul>
            <p className="text-slate-700 mb-4">
              To exercise your CCPA rights, visit your <a href="/privacy-settings" className="text-cyan-600 hover:underline">Privacy Settings</a> page or contact us at info@georgedives.com.
            </p>

            {/* Email Marketing */}
            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-12">Email Marketing & Communications</h2>
            <p className="text-slate-700 mb-4">
              If we send you marketing emails, we comply with CAN-SPAM and other anti-spam laws:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
              <li><strong>Unsubscribe:</strong> Every marketing email includes an unsubscribe link</li>
              <li><strong>Opt-out honored:</strong> We process unsubscribe requests within 10 business days</li>
              <li><strong>Clear identification:</strong> All emails clearly identify George Dives as the sender</li>
              <li><strong>Physical address:</strong> Our mailing address is included in marketing emails</li>
              <li><strong>Accurate subject lines:</strong> Subject lines accurately reflect email content</li>
            </ul>
            <p className="text-slate-700 mb-4">
              You can manage your email preferences or unsubscribe at any time by clicking the link in any email or contacting us directly.
            </p>

            <div className="mt-12 p-6 bg-blue-50 border-l-4 border-blue-500 rounded">
              <p className="text-sm text-slate-700">
                <strong>Your Privacy Matters:</strong> We are committed to protecting your personal information and ensuring transparency in how we use your data. If you have any concerns, please don't hesitate to reach out.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} George Dives. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;